import { useRef, useState, type CSSProperties, type JSX, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { libraryApi, type LibraryItem, type TrashItem } from '../api/library.js';
import { useApp } from '../state/app.js';
import { ConfirmDialog, Spinner } from '../ui/common.js';
import {
  ArrowClockwiseRegular, ArrowDownloadRegular, ArrowLeftRegular, ArrowResetRegular, ArrowSortRegular, ArrowUpRegular, ArrowUploadRegular,
  CheckmarkRegular, ChevronDownRegular, DeleteRegular, FolderAddRegular, FolderOpenRegular, GridRegular, PanelRightRegular, RenameRegular,
} from '@fluentui/react-icons';

const ic = (size = 16) => ({ fontSize: size, flexShrink: 0 }) as const;

const MAX_BYTES = 500 * 1024 * 1024; // 서버와 같은 한도
const BAD_NAME = /[\\/:*?"<>|#%]/;
const NAME_RULE = '이름에 \\ / : * ? " < > | # % 는 쓸 수 없습니다.';

type SortKey = 'name' | 'modified' | 'type' | 'size' | 'owner';
type ViewMode = 'large' | 'medium' | 'list' | 'details';
interface Prefs { sort: SortKey; asc: boolean; view: ViewMode; pane: boolean; }
const SORT_LABELS: Record<SortKey, string> = { name: '이름', modified: '수정한 날짜', type: '유형', size: '크기', owner: '소유자' };
const VIEW_LABELS: Record<ViewMode, string> = { large: '큰 아이콘', medium: '보통 아이콘', list: '목록', details: '자세히' };

// 정렬·보기·세부 정보 창 설정은 브라우저별로 기억(스토리지 차단 시 기본값)
const PREFS_KEY = 'dx-library-prefs';
const DEFAULT_PREFS: Prefs = { sort: 'name', asc: true, view: 'details', pane: false };
function loadPrefs(): Prefs {
  try { return { ...DEFAULT_PREFS, ...(JSON.parse(localStorage.getItem(PREFS_KEY) ?? '{}') as Partial<Prefs>) }; } catch { return DEFAULT_PREFS; }
}

function fmtSize(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${Math.ceil(n / 1024).toLocaleString()} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
function fmtDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (x: number) => String(x).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
}
const extOf = (name: string) => (name.includes('.') ? name.split('.').pop()!.toUpperCase() : '');
const typeOf = (it: LibraryItem) => (it.folder ? '파일 폴더' : extOf(it.name) ? `${extOf(it.name)} 파일` : '파일');
const ownerOf = (it: LibraryItem) => it.ownerName || '—';

/** 탐색기와 같이 폴더가 항상 먼저, 그 안에서 선택한 기준·방향으로 정렬 */
function sortItems(items: LibraryItem[], key: SortKey, asc: boolean): LibraryItem[] {
  const byName = (a: LibraryItem, b: LibraryItem) => a.name.localeCompare(b.name, 'ko', { numeric: true });
  const cmp: Record<SortKey, (a: LibraryItem, b: LibraryItem) => number> = {
    name: byName,
    modified: (a, b) => a.modifiedAt.localeCompare(b.modifiedAt) || byName(a, b),
    type: (a, b) => typeOf(a).localeCompare(typeOf(b), 'ko') || byName(a, b),
    size: (a, b) => a.size - b.size || byName(a, b),
    owner: (a, b) => (a.ownerName ?? '').localeCompare(b.ownerName ?? '', 'ko') || byName(a, b),
  };
  return [...items].sort((a, b) => (a.folder !== b.folder ? (a.folder ? -1 : 1) : cmp[key](a, b) * (asc ? 1 : -1)));
}

const errMsg = (e: unknown, fallback: string) => (e instanceof Error ? e.message : fallback);

const FolderIcon = ({ size = 18 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}><path d="M2 6.5A1.5 1.5 0 0 1 3.5 5h5.2l2 2.2h9.8A1.5 1.5 0 0 1 22 8.7v9.8a1.5 1.5 0 0 1-1.5 1.5h-17A1.5 1.5 0 0 1 2 18.5z" fill="#F5C147" /><path d="M2 9.5h20v9a1.5 1.5 0 0 1-1.5 1.5h-17A1.5 1.5 0 0 1 2 18.5z" fill="#FFD666" /></svg>
);
const FILE_COLORS: Record<string, string> = { PDF: '#DC2626', HWP: '#0EA5E9', HWPX: '#0EA5E9', DOC: '#2563EB', DOCX: '#2563EB', XLS: '#16A34A', XLSX: '#16A34A', PPT: '#EA580C', PPTX: '#EA580C', ZIP: '#7C3AED' };
const FileIcon = ({ name, size = 18 }: { name: string; size?: number }) => {
  const ext = extOf(name);
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden style={{ flexShrink: 0 }}>
      <path d="M6 2.5h8l4.5 4.5v14a.5.5 0 0 1-.5.5H6a.5.5 0 0 1-.5-.5V3a.5.5 0 0 1 .5-.5z" fill="#fff" stroke="#9CA3AF" strokeWidth={size > 30 ? 0.5 : 1} />
      <path d="M14 2.5V7h4.5" fill="none" stroke="#9CA3AF" strokeWidth={size > 30 ? 0.5 : 1} />
      <rect x="7" y="12" width="10" height="5" rx="0.8" fill={FILE_COLORS[ext] ?? '#9CA3AF'} />
      {size > 30 && ext && <text x="12" y="15.6" textAnchor="middle" fontSize="2.9" fontWeight="700" fill="#fff" fontFamily="sans-serif">{ext.slice(0, 4)}</text>}
    </svg>
  );
};
const ItemIcon = ({ it, size }: { it: LibraryItem; size: number }) => (it.folder ? <FolderIcon size={size} /> : <FileIcon name={it.name} size={size} />);
const TrashIcon = ({ size = 16 }: { size?: number }) => <DeleteRegular aria-hidden style={ic(size)} />;

const navBtn = (disabled: boolean): CSSProperties => ({
  width: 30, height: 30, border: 'none', borderRadius: 6, background: 'transparent', cursor: disabled ? 'default' : 'pointer',
  color: disabled ? '#D1D5DB' : '#374151', fontSize: 16, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
});
const cmdBtn = (disabled = false, active = false): CSSProperties => ({
  display: 'inline-flex', alignItems: 'center', gap: 6, border: 'none', borderRadius: 6, padding: '6px 10px', background: active ? '#EAF2FF' : 'transparent',
  fontFamily: 'inherit', fontSize: 13, fontWeight: 600, color: disabled ? '#C0C4CB' : active ? '#1D4ED8' : '#374151', cursor: disabled ? 'default' : 'pointer',
});
const sep = <span style={{ width: 1, height: 18, background: '#E5E7EB', margin: '0 6px' }} />;
const GRID = 'minmax(0,1fr) 140px 100px 84px 110px';
const ICON_VIEW: Record<'large' | 'medium', { cell: number; icon: number }> = { large: { cell: 124, icon: 76 }, medium: { cell: 96, icon: 48 } };

/** 탐색기 명령 모음의 드롭다운(정렬 ▾ / 보기 ▾) — 바깥 클릭 시 닫힘 */
function Dropdown({ label, children }: { label: ReactNode; children: (close: () => void) => ReactNode }): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <span style={{ position: 'relative' }}>
      <button style={cmdBtn(false, open)} onClick={() => setOpen((o) => !o)} aria-expanded={open}>{label} <ChevronDownRegular aria-hidden style={{ fontSize: 12, color: '#9CA3AF' }} /></button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={() => setOpen(false)} />
          <div role="menu" style={{ ...menuBox, position: 'absolute', top: 'calc(100% + 4px)', left: 0 }}>{children(() => setOpen(false))}</div>
        </>
      )}
    </span>
  );
}
const menuBox: CSSProperties = { zIndex: 41, minWidth: 180, background: '#fff', border: '1px solid #E5E7EB', borderRadius: 8, boxShadow: '0 10px 30px rgba(16,24,40,.16)', padding: 4 };
function MenuItem({ checked, disabled, danger, onClick, children }: { checked?: boolean; disabled?: boolean; danger?: boolean; onClick: () => void; children: ReactNode }): JSX.Element {
  return (
    <button role={checked === undefined ? 'menuitem' : 'menuitemradio'} aria-checked={checked} disabled={disabled} onClick={onClick}
      style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', border: 'none', background: 'transparent', textAlign: 'left', padding: '7px 10px', borderRadius: 5, fontFamily: 'inherit', fontSize: 13, cursor: disabled ? 'default' : 'pointer', color: disabled ? '#C0C4CB' : danger ? '#DC2626' : '#1F2937' }}
      onMouseEnter={(e) => { if (!disabled) e.currentTarget.style.background = '#F3F4F6'; }} onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
      <span style={{ width: 14, color: '#2563EB', display: 'inline-flex' }}>{checked ? <CheckmarkRegular aria-hidden style={ic(14)} /> : null}</span>{children}
    </button>
  );
}
const MenuSep = () => <div style={{ height: 1, background: '#EEF0F3', margin: '4px 6px' }} />;

/** 우클릭 메뉴 — 마우스 위치에 띄우되 화면 밖으로 나가지 않게 보정 */
function ContextMenu({ x, y, onClose, children }: { x: number; y: number; onClose: () => void; children: ReactNode }): JSX.Element {
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, zIndex: 40 }} onClick={onClose} onContextMenu={(e) => { e.preventDefault(); onClose(); }} />
      <div role="menu" style={{ ...menuBox, position: 'fixed', left: Math.min(x, window.innerWidth - 200), top: Math.min(y, window.innerHeight - 240) }}
        onClick={(e) => e.stopPropagation()}>{children}</div>
    </>
  );
}

/** 이름 입력(새 폴더·이름 바꾸기) — 포커스 시 확장자 앞까지만 선택(탐색기와 동일) */
function NameInput({ value, onChange, onCommit, onCancel, width, center }: {
  value: string; onChange: (v: string) => void; onCommit: () => void; onCancel: () => void; width: number | string; center?: boolean;
}): JSX.Element {
  return (
    <input autoFocus value={value} onChange={(e) => onChange(e.target.value)}
      onFocus={(e) => { const dot = e.target.value.lastIndexOf('.'); e.target.setSelectionRange(0, dot > 0 ? dot : e.target.value.length); }}
      onKeyDown={(e) => { e.stopPropagation(); if (e.key === 'Enter') onCommit(); if (e.key === 'Escape') onCancel(); }}
      onClick={(e) => e.stopPropagation()} onDoubleClick={(e) => e.stopPropagation()}
      style={{ width, height: 24, border: '1px solid #2563EB', borderRadius: 3, padding: '0 6px', fontFamily: 'inherit', fontSize: 13, textAlign: center ? 'center' : 'left' }} />
  );
}

// 자료실 — 윈도우 탐색기 형식. 임직원 누구나 폴더 생성·업로드, 이름 변경·삭제는 소유자·관리자(삭제는 휴지통으로).
export function Library(): JSX.Element {
  const { me, flash } = useApp();
  const isAdmin = me.permissions.canManageMembers;
  const qc = useQueryClient();
  const [trashMode, setTrashMode] = useState(false);
  const [segs, setSegs] = useState<string[]>([]);
  const [history, setHistory] = useState<string[][]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [newFolder, setNewFolder] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<{ id: string; value: string } | null>(null);
  const [ctx, setCtx] = useState<{ x: number; y: number; item: LibraryItem | null } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [drag, setDrag] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<LibraryItem | null>(null);
  const [prefs, setPrefsState] = useState<Prefs>(loadPrefs);
  const fileInput = useRef<HTMLInputElement>(null);

  const setPrefs = (p: Partial<Prefs>) => setPrefsState((cur) => {
    const next = { ...cur, ...p };
    try { localStorage.setItem(PREFS_KEY, JSON.stringify(next)); } catch { /* 저장 불가 시 이번 세션만 */ }
    return next;
  });
  // 자세히 보기의 열 머리글 클릭: 같은 열이면 방향 전환, 다른 열이면 그 열 오름차순
  const sortBy = (k: SortKey) => setPrefs(prefs.sort === k ? { asc: !prefs.asc } : { sort: k, asc: true });

  const path = segs.join('/');
  const q = useQuery({ queryKey: ['library', path], queryFn: () => libraryApi.list(path), enabled: !trashMode });
  const all = q.data?.items ?? [];
  const items = sortItems(all.filter((i) => i.name.toLowerCase().includes(filter.trim().toLowerCase())), prefs.sort, prefs.asc);
  const sel = items.find((i) => i.id === selected) ?? null;
  const refresh = () => qc.invalidateQueries({ queryKey: ['library', path] });
  // 서버와 같은 규칙: 소유자(대소문자 무시) 또는 관리자. 소유자 기록 없는 항목은 관리자만
  const canEdit = (it: LibraryItem | null) => !!it && (isAdmin || (!!it.owner && it.owner.toLowerCase() === me.upn.toLowerCase()));

  const reset = () => { setSelected(null); setFilter(''); setNewFolder(null); setRenaming(null); };
  const navigate = (next: string[]) => { setHistory((h) => [...h, segs]); setSegs(next); reset(); };
  const back = () => {
    const prev = history[history.length - 1];
    if (!prev) return;
    setHistory((h) => h.slice(0, -1));
    setSegs(prev); reset();
  };
  const open = (it: LibraryItem) => {
    if (it.folder) navigate([...segs, it.name]);
    else void libraryApi.download(it.id, it.name).catch((e) => flash(errMsg(e, '다운로드 실패')));
  };

  const run = async (label: string, fn: () => Promise<void>, fallback: string) => {
    setBusy(label);
    try { await fn(); } catch (e) { flash(errMsg(e, fallback)); } finally { setBusy(null); await refresh(); }
  };

  const createFolder = () => {
    const name = (newFolder ?? '').trim();
    if (!name) { setNewFolder(null); return; }
    if (BAD_NAME.test(name)) { flash(NAME_RULE); return; }
    void run('폴더 만드는 중…', async () => {
      await libraryApi.createFolder(path, name);
      setNewFolder(null); flash(`'${name}' 폴더를 만들었습니다`);
    }, '폴더 생성 실패');
  };

  const commitRename = () => {
    if (!renaming) return;
    const it = all.find((i) => i.id === renaming.id);
    const name = renaming.value.trim();
    if (!it || !name || name === it.name) { setRenaming(null); return; }
    if (BAD_NAME.test(name)) { flash(NAME_RULE); return; }
    void run('이름 바꾸는 중…', async () => {
      await libraryApi.rename(it.id, name);
      setRenaming(null); flash(`'${name}'(으)로 이름을 바꿨습니다`);
    }, '이름 바꾸기 실패');
  };
  const startRename = (it: LibraryItem) => { setSelected(it.id); setNewFolder(null); setRenaming({ id: it.id, value: it.name }); };

  const upload = async (files: FileList | File[]) => {
    const arr = Array.from(files);
    if (!arr.length) return;
    let ok = 0;
    await run('업로드 중…', async () => {
      for (const [i, f] of arr.entries()) {
        if (f.size > MAX_BYTES) { flash(`${f.name}: 500MB 초과로 제외`); continue; }
        setBusy(`업로드 중… (${i + 1}/${arr.length}) ${f.name}`);
        await libraryApi.upload(path, f, (ratio) => setBusy(`업로드 중… (${i + 1}/${arr.length}) ${f.name} ${Math.round(ratio * 100)}%`));
        ok++;
      }
      if (ok) flash(`${ok}개 파일을 업로드했습니다`);
    }, '업로드 실패');
  };

  const remove = (it: LibraryItem) => {
    setPendingDelete(null);
    void run('휴지통으로 이동 중…', async () => {
      await libraryApi.remove(it.id);
      setSelected(null); flash(`'${it.name}'을(를) 휴지통으로 옮겼습니다`);
      void qc.invalidateQueries({ queryKey: ['library-trash'] });
    }, '삭제 실패');
  };

  const onKey = (e: KeyboardEvent) => {
    if ((e.target as HTMLElement).tagName === 'INPUT') return;
    if (e.key === 'Enter' && sel) open(sel);
    else if (e.key === 'F2' && canEdit(sel)) { e.preventDefault(); startRename(sel!); }
    else if (e.key === 'Backspace' && segs.length) { e.preventDefault(); navigate(segs.slice(0, -1)); }
    else if (e.key === 'Delete' && canEdit(sel)) setPendingDelete(sel);
  };

  // 화면 제목은 상단바, 사용 규칙은 도움말(?)에 있어 본문 머리글은 두지 않음
  const header = null;
  const wrap: CSSProperties = { maxWidth: 1180, margin: '0 auto', animation: 'dxfade .3s ease', display: 'flex', flexDirection: 'column', gap: 14 };

  if (trashMode) {
    return (
      <div style={wrap}>
        {header}
        <TrashView isAdmin={isAdmin} myUpn={me.upn} onExit={() => { setTrashMode(false); void refresh(); }} />
      </div>
    );
  }

  const notFound = q.error instanceof Error;
  const view = prefs.view;
  const iconView = view === 'large' || view === 'medium' ? ICON_VIEW[view] : null;

  // 항목 공통 이벤트(클릭=선택, 더블클릭=열기/다운로드, 우클릭=메뉴) + 선택·호버 배경
  const itemProps = (it: LibraryItem) => {
    const on = it.id === selected;
    return {
      role: 'option', 'aria-selected': on, title: `${it.name}\n소유자: ${ownerOf(it)}`,
      onClick: (e: MouseEvent) => { e.stopPropagation(); setSelected(it.id); },
      onDoubleClick: () => { if (renaming?.id !== it.id) open(it); },
      onContextMenu: (e: MouseEvent) => { e.preventDefault(); e.stopPropagation(); setSelected(it.id); setCtx({ x: e.clientX, y: e.clientY, item: it }); },
      onMouseEnter: (e: MouseEvent<HTMLDivElement>) => { if (!on) e.currentTarget.style.background = '#F3F7FD'; },
      onMouseLeave: (e: MouseEvent<HTMLDivElement>) => { if (!on) e.currentTarget.style.background = ''; },
      style: { borderRadius: 4, cursor: 'default', userSelect: 'none', fontSize: 13, background: on ? '#DCEBFF' : undefined } as CSSProperties,
    };
  };
  // 이름 칸: 이름 바꾸는 중이면 입력창, 아니면 텍스트
  const nameOf = (it: LibraryItem, style: CSSProperties, width: number | string) => (renaming?.id === it.id
    ? <NameInput value={renaming.value} onChange={(v) => setRenaming({ id: it.id, value: v })} onCommit={commitRename} onCancel={() => setRenaming(null)} width={width} center={!!iconView} />
    : <span style={style}>{it.name}</span>);
  const newFolderInput = (width: number | string) => (
    <NameInput value={newFolder ?? ''} onChange={setNewFolder} onCommit={createFolder} onCancel={() => setNewFolder(null)} width={width} center={!!iconView} />
  );
  const sortArrow = (k: SortKey) => (prefs.sort === k ? <ArrowUpRegular aria-hidden style={{ fontSize: 12, marginLeft: 4, verticalAlign: '-2px', transform: prefs.asc ? 'none' : 'rotate(180deg)' }} /> : null);
  const ellipsis: CSSProperties = { whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  let body: ReactNode;
  if (q.isLoading) body = <div style={{ padding: 40, textAlign: 'center' }}><Spinner size={22} /></div>;
  else if (notFound) body = <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>{(q.error as Error).message}</div>;
  else if (items.length === 0 && newFolder === null) {
    body = (
      <div style={{ padding: 48, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>
        {filter ? '검색 결과가 없습니다.' : '이 폴더는 비어 있습니다.'}
        {!filter && <div style={{ fontSize: 12, marginTop: 6, color: '#C0C4CB' }}>파일을 여기로 끌어다 놓거나 “업로드”를 누르세요 · 1개 최대 500MB</div>}
      </div>
    );
  } else if (iconView) {
    const tile: CSSProperties = { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, padding: '10px 4px 8px', textAlign: 'center' };
    const label: CSSProperties = { width: '100%', lineHeight: 1.35, wordBreak: 'break-all', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' };
    body = (
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${iconView.cell}px, 1fr))`, gap: 4, padding: 6 }}>
        {newFolder !== null && <div style={{ ...tile, background: '#EAF2FF', borderRadius: 4 }}><FolderIcon size={iconView.icon} />{newFolderInput('100%')}</div>}
        {items.map((it) => {
          const p = itemProps(it);
          return <div key={it.id} {...p} style={{ ...p.style, ...tile }}><ItemIcon it={it} size={iconView.icon} />{nameOf(it, label, '100%')}</div>;
        })}
      </div>
    );
  } else if (view === 'list') {
    const row: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 8px', minWidth: 0 };
    body = (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: 1, padding: 6 }}>
        {newFolder !== null && <div style={{ ...row, background: '#EAF2FF', borderRadius: 4 }}><FolderIcon />{newFolderInput('100%')}</div>}
        {items.map((it) => {
          const p = itemProps(it);
          return <div key={it.id} {...p} style={{ ...p.style, ...row }}><ItemIcon it={it} size={18} />{nameOf(it, ellipsis, '100%')}</div>;
        })}
      </div>
    );
  } else {
    const cell: CSSProperties = { color: '#6B7280', fontSize: 12.5, ...ellipsis };
    body = (
      <div style={{ minWidth: 620 }}>
        <div style={{ display: 'grid', gridTemplateColumns: GRID, padding: '4px 14px', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F3F5' }}>
          {(['name', 'modified', 'type', 'size', 'owner'] as SortKey[]).map((k) => (
            <button key={k} onClick={() => sortBy(k)}
              style={{ border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 12, color: prefs.sort === k ? '#1D4ED8' : '#6B7280', padding: '3px 0', cursor: 'pointer', textAlign: k === 'size' ? 'right' : 'left', paddingLeft: k === 'owner' ? 14 : 0, fontWeight: prefs.sort === k ? 700 : 500 }}>
              {SORT_LABELS[k]}{sortArrow(k)}
            </button>
          ))}
        </div>
        <div style={{ padding: '4px 6px' }}>
          {newFolder !== null && (
            <div style={{ display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', padding: '5px 8px', background: '#EAF2FF', borderRadius: 4 }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}><FolderIcon />{newFolderInput(240)}</span>
              <span /><span style={cell}>파일 폴더</span><span /><span style={{ ...cell, paddingLeft: 14 }}>{me.name}</span>
            </div>
          )}
          {items.map((it) => {
            const p = itemProps(it);
            return (
              <div key={it.id} {...p} style={{ ...p.style, display: 'grid', gridTemplateColumns: GRID, alignItems: 'center', padding: '5px 8px' }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                  <ItemIcon it={it} size={18} />{nameOf(it, ellipsis, 260)}
                </span>
                <span style={cell}>{fmtDate(it.modifiedAt)}</span>
                <span style={cell}>{typeOf(it)}</span>
                <span style={{ ...cell, textAlign: 'right' }}>{it.folder ? '' : fmtSize(it.size)}</span>
                <span style={{ ...cell, paddingLeft: 14 }} title={it.owner}>{ownerOf(it)}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // 세부 정보 창 — 선택 항목, 없으면 현재 폴더
  const here = segs[segs.length - 1] ?? '자료실';
  const detailRows: Array<[string, string]> = sel
    ? [
        ['유형', typeOf(sel)],
        sel.folder ? ['항목 수', `${sel.childCount ?? 0}개`] : ['크기', `${fmtSize(sel.size)} (${sel.size.toLocaleString()} 바이트)`],
        ['소유자', sel.ownerName ? `${sel.ownerName} (${sel.owner})` : '기록 없음 (관리자만 수정)'],
        ['수정한 날짜', fmtDate(sel.modifiedAt)],
        ['위치', ['자료실', ...segs].join(' › ')],
      ]
    : [
        ['유형', '파일 폴더'],
        ['항목 수', `폴더 ${all.filter((i) => i.folder).length}개 · 파일 ${all.filter((i) => !i.folder).length}개`],
        ['파일 크기 합계', fmtSize(all.reduce((s, i) => s + (i.folder ? 0 : i.size), 0))],
        ['위치', ['자료실', ...segs.slice(0, -1)].join(' › ') || '자료실'],
      ];

  const ctxItem = ctx?.item ?? null;

  return (
    <div style={wrap}>
      {header}

      <div className="dx-card" tabIndex={0} onKeyDown={onKey} style={{ overflow: 'hidden', outline: 'none' }}>
        {/* 주소 표시줄 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '8px 10px', borderBottom: '1px solid #EEF0F3', background: '#FAFBFC' }}>
          <button title="뒤로" style={navBtn(!history.length)} disabled={!history.length} onClick={back} aria-label="뒤로"><ArrowLeftRegular style={ic()} /></button>
          <button title="상위 폴더" style={navBtn(!segs.length)} disabled={!segs.length} onClick={() => navigate(segs.slice(0, -1))} aria-label="상위 폴더"><ArrowUpRegular style={ic()} /></button>
          <button title="새로 고침" style={navBtn(false)} onClick={() => void refresh()} aria-label="새로 고침"><ArrowClockwiseRegular style={ic()} /></button>
          <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2, height: 32, padding: '0 8px', margin: '0 6px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', overflowX: 'auto', whiteSpace: 'nowrap' }}>
            <FolderIcon size={16} />
            {['자료실', ...segs].map((s, i) => (
              <span key={i} style={{ display: 'inline-flex', alignItems: 'center' }}>
                {i > 0 && <span style={{ color: '#9CA3AF', margin: '0 2px' }}>›</span>}
                <button onClick={() => i !== segs.length && navigate(segs.slice(0, i))}
                  style={{ border: 'none', background: 'transparent', fontFamily: 'inherit', fontSize: 13, padding: '3px 5px', borderRadius: 4, cursor: 'pointer', color: '#1F2937', fontWeight: i === segs.length ? 700 : 500 }}>{s}</button>
              </span>
            ))}
          </div>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder={`${here} 검색`}
            style={{ width: 200, maxWidth: '30%', height: 32, border: '1px solid #E5E7EB', borderRadius: 6, padding: '0 10px', fontFamily: 'inherit', fontSize: 13 }} />
        </div>

        {/* 명령 모음 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '6px 10px', borderBottom: '1px solid #EEF0F3', flexWrap: 'wrap' }}>
          <button style={cmdBtn(!!busy || notFound)} disabled={!!busy || notFound} onClick={() => { setRenaming(null); setNewFolder('새 폴더'); setSelected(null); }}><FolderAddRegular style={ic()} /> 새 폴더</button>
          <button style={cmdBtn(!!busy || notFound)} disabled={!!busy || notFound} onClick={() => fileInput.current?.click()}><ArrowUploadRegular style={ic()} /> 업로드</button>
          <input ref={fileInput} type="file" hidden multiple onChange={(e) => { if (e.target.files?.length) void upload(e.target.files); e.target.value = ''; }} />
          {sep}
          <button style={cmdBtn(!sel)} disabled={!sel} onClick={() => sel && open(sel)}>{sel?.folder ? <><FolderOpenRegular style={ic()} /> 열기</> : <><ArrowDownloadRegular style={ic()} /> 다운로드</>}</button>
          <button style={cmdBtn(!canEdit(sel) || !!busy)} disabled={!canEdit(sel) || !!busy} title="이름 바꾸기 (F2)" onClick={() => sel && startRename(sel)}><RenameRegular style={ic()} /> 이름 바꾸기</button>
          <button style={{ ...cmdBtn(!canEdit(sel) || !!busy), color: !canEdit(sel) || busy ? '#C0C4CB' : '#DC2626' }} disabled={!canEdit(sel) || !!busy} title="휴지통으로 이동 (Delete)"
            onClick={() => sel && setPendingDelete(sel)}><TrashIcon /> 삭제</button>
          {sep}
          <Dropdown label={<><ArrowSortRegular style={ic()} /> 정렬</>}>
            {(close) => (
              <>
                {(Object.keys(SORT_LABELS) as SortKey[]).map((k) => (
                  <MenuItem key={k} checked={prefs.sort === k} onClick={() => { setPrefs({ sort: k }); close(); }}>{SORT_LABELS[k]}</MenuItem>
                ))}
                <MenuSep />
                <MenuItem checked={prefs.asc} onClick={() => { setPrefs({ asc: true }); close(); }}>오름차순</MenuItem>
                <MenuItem checked={!prefs.asc} onClick={() => { setPrefs({ asc: false }); close(); }}>내림차순</MenuItem>
              </>
            )}
          </Dropdown>
          <Dropdown label={<><GridRegular style={ic()} /> 보기</>}>
            {(close) => (Object.keys(VIEW_LABELS) as ViewMode[]).map((v) => (
              <MenuItem key={v} checked={view === v} onClick={() => { setPrefs({ view: v }); close(); }}>{VIEW_LABELS[v]}</MenuItem>
            ))}
          </Dropdown>
          <span style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {busy && <span style={{ fontSize: 12, color: '#2563EB', display: 'inline-flex', alignItems: 'center', gap: 6, marginRight: 6 }}><Spinner size={14} />{busy}</span>}
            <button style={cmdBtn()} onClick={() => { reset(); setTrashMode(true); }}><TrashIcon /> 휴지통</button>
            <button style={cmdBtn(false, prefs.pane)} aria-pressed={prefs.pane} onClick={() => setPrefs({ pane: !prefs.pane })}><PanelRightRegular style={ic()} /> 세부 정보</button>
          </span>
        </div>

        <div style={{ display: 'flex', minHeight: 360 }}>
          {/* 항목 영역(빈 곳 우클릭=폴더 메뉴, 파일 끌어다 놓기=업로드) */}
          <div
            onClick={() => setSelected(null)}
            onContextMenu={(e) => { e.preventDefault(); setSelected(null); setCtx({ x: e.clientX, y: e.clientY, item: null }); }}
            onDragOver={(e) => { e.preventDefault(); if (!busy && !notFound) setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); if (!busy && !notFound && e.dataTransfer.files?.length) void upload(e.dataTransfer.files); }}
            style={{ flex: 1, minWidth: 0, overflowX: 'auto', background: drag ? '#EFF6FF' : '#fff', outline: drag ? '2px dashed #2563EB' : 'none', outlineOffset: -6 }}>
            {body}
          </div>

          {/* 세부 정보 창 */}
          {prefs.pane && (
            <aside style={{ width: 260, flexShrink: 0, borderLeft: '1px solid #EEF0F3', background: '#FAFBFC', padding: '20px 16px', display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10, textAlign: 'center' }}>
                {sel ? <ItemIcon it={sel} size={84} /> : <FolderIcon size={84} />}
                <div style={{ fontSize: 14, fontWeight: 700, wordBreak: 'break-all' }}>{sel ? sel.name : here}</div>
              </div>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#6B7280', borderTop: '1px solid #EEF0F3', paddingTop: 12 }}>속성</div>
              <dl style={{ margin: 0, display: 'flex', flexDirection: 'column', gap: 10 }}>
                {detailRows.map(([k, v]) => (
                  <div key={k}>
                    <dt style={{ fontSize: 11.5, color: '#9CA3AF' }}>{k}</dt>
                    <dd style={{ margin: '2px 0 0', fontSize: 12.5, color: '#1F2937', wordBreak: 'break-all' }}>{v}</dd>
                  </div>
                ))}
              </dl>
              {sel && <button className="dx-btn dx-btn-soft" style={{ marginTop: 'auto', fontSize: 12.5 }} onClick={() => open(sel)}>{sel.folder ? '폴더 열기' : '다운로드'}</button>}
            </aside>
          )}
        </div>

        {/* 상태 표시줄 */}
        <div style={{ display: 'flex', padding: '6px 14px', borderTop: '1px solid #EEF0F3', fontSize: 12, color: '#6B7280', background: '#FAFBFC' }}>
          <span>항목 {items.length}개{sel ? ` · 1개 항목 선택함${sel.folder ? '' : ` · ${fmtSize(sel.size)}`}` : ''}</span>
          <span style={{ marginLeft: 'auto' }}>{VIEW_LABELS[view]} · {SORT_LABELS[prefs.sort]} {prefs.asc ? '오름차순' : '내림차순'}</span>
        </div>
      </div>

      {ctx && (
        <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)}>
          {ctxItem ? (
            <>
              <MenuItem onClick={() => { setCtx(null); open(ctxItem); }}>{ctxItem.folder ? <><FolderOpenRegular style={ic(14)} /> 열기</> : <><ArrowDownloadRegular style={ic(14)} /> 다운로드</>}</MenuItem>
              <MenuSep />
              <MenuItem disabled={!canEdit(ctxItem) || !!busy} onClick={() => { setCtx(null); startRename(ctxItem); }}><RenameRegular style={ic(14)} /> 이름 바꾸기 <span style={{ marginLeft: 'auto', color: '#9CA3AF', fontSize: 11 }}>F2</span></MenuItem>
              <MenuItem danger disabled={!canEdit(ctxItem) || !!busy} onClick={() => { setCtx(null); setPendingDelete(ctxItem); }}><TrashIcon size={14} /> 삭제 <span style={{ marginLeft: 'auto', color: '#9CA3AF', fontSize: 11 }}>Del</span></MenuItem>
              <MenuSep />
              <MenuItem onClick={() => { setCtx(null); setPrefs({ pane: true }); }}><PanelRightRegular style={ic(14)} /> 속성 (세부 정보)</MenuItem>
              {!canEdit(ctxItem) && <div style={{ padding: '4px 10px 6px 32px', fontSize: 11, color: '#9CA3AF' }}>이름 바꾸기·삭제는 소유자·관리자만</div>}
            </>
          ) : (
            <>
              <MenuItem disabled={!!busy || notFound} onClick={() => { setCtx(null); setRenaming(null); setNewFolder('새 폴더'); }}>새 폴더</MenuItem>
              <MenuItem disabled={!!busy || notFound} onClick={() => { setCtx(null); fileInput.current?.click(); }}>업로드</MenuItem>
              <MenuSep />
              <MenuItem onClick={() => { setCtx(null); void refresh(); }}>새로 고침</MenuItem>
            </>
          )}
        </ContextMenu>
      )}

      {pendingDelete && (
        <ConfirmDialog title={pendingDelete.folder ? '폴더 삭제' : '파일 삭제'} danger confirmLabel="휴지통으로 이동"
          message={pendingDelete.folder ? `'${pendingDelete.name}' 폴더와 그 안의 모든 항목을 휴지통으로 옮길까요? 휴지통에서 복원할 수 있습니다.` : `'${pendingDelete.name}' 파일을 휴지통으로 옮길까요? 휴지통에서 복원할 수 있습니다.`}
          onConfirm={() => remove(pendingDelete)} onCancel={() => setPendingDelete(null)} />
      )}
    </div>
  );
}

const TRASH_GRID = 'minmax(0,1fr) 170px 140px 110px 84px';

/** 휴지통 — 복원(소유자·삭제한 사람·관리자), 영구 삭제·비우기(관리자) */
function TrashView({ isAdmin, myUpn, onExit }: { isAdmin: boolean; myUpn: string; onExit: () => void }): JSX.Element {
  const { flash } = useApp();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['library-trash'], queryFn: () => libraryApi.trash() });
  const items = q.data?.items ?? [];
  const [selected, setSelected] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [ctx, setCtx] = useState<{ x: number; y: number; item: TrashItem } | null>(null);
  const [confirm, setConfirm] = useState<{ kind: 'purge'; item: TrashItem } | { kind: 'empty' } | null>(null);
  const sel = items.find((i) => i.id === selected) ?? null;
  const me = myUpn.toLowerCase();
  const canRestore = (it: TrashItem | null) => !!it && (isAdmin || it.owner?.toLowerCase() === me || it.deletedBy.toLowerCase() === me);
  const refresh = () => qc.invalidateQueries({ queryKey: ['library-trash'] });

  const run = async (label: string, fn: () => Promise<void>, fallback: string) => {
    setBusy(label);
    try { await fn(); } catch (e) { flash(errMsg(e, fallback)); } finally { setBusy(null); setSelected(null); await refresh(); }
  };
  const restore = (it: TrashItem) => void run('복원 중…', async () => {
    const { path } = await libraryApi.restore(it.id);
    flash(`'${it.originalName}'을(를) ${['자료실', ...path.split('/').filter(Boolean)].join(' › ')}(으)로 복원했습니다`);
    void qc.invalidateQueries({ queryKey: ['library'] });
  }, '복원 실패');
  const purge = (it: TrashItem) => void run('영구 삭제 중…', async () => {
    await libraryApi.purge(it.id); flash(`'${it.originalName}'을(를) 영구 삭제했습니다`);
  }, '영구 삭제 실패');
  const empty = () => void run('휴지통 비우는 중…', async () => {
    for (const it of items) await libraryApi.purge(it.id);
    flash('휴지통을 비웠습니다');
  }, '휴지통 비우기 실패');

  const cell: CSSProperties = { color: '#6B7280', fontSize: 12.5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' };

  return (
    <div className="dx-card" style={{ overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderBottom: '1px solid #EEF0F3', background: '#FAFBFC' }}>
        <button title="자료실로 돌아가기" aria-label="자료실로 돌아가기" style={navBtn(false)} onClick={onExit}><ArrowLeftRegular style={ic()} /></button>
        <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, height: 32, padding: '0 10px', border: '1px solid #E5E7EB', borderRadius: 6, background: '#fff', fontSize: 13, fontWeight: 700, color: '#374151' }}>
          <TrashIcon /> 휴지통
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '6px 10px', borderBottom: '1px solid #EEF0F3', flexWrap: 'wrap' }}>
        <button style={cmdBtn(!canRestore(sel) || !!busy)} disabled={!canRestore(sel) || !!busy} onClick={() => sel && restore(sel)}><ArrowResetRegular style={ic()} /> 복원</button>
        {isAdmin && (
          <>
            <button style={{ ...cmdBtn(!sel || !!busy), color: !sel || busy ? '#C0C4CB' : '#DC2626' }} disabled={!sel || !!busy} onClick={() => sel && setConfirm({ kind: 'purge', item: sel })}>영구 삭제</button>
            {sep}
            <button style={{ ...cmdBtn(!items.length || !!busy), color: !items.length || busy ? '#C0C4CB' : '#DC2626' }} disabled={!items.length || !!busy} onClick={() => setConfirm({ kind: 'empty' })}>휴지통 비우기</button>
          </>
        )}
        {busy && <span style={{ marginLeft: 'auto', fontSize: 12, color: '#2563EB', display: 'inline-flex', alignItems: 'center', gap: 6 }}><Spinner size={14} />{busy}</span>}
      </div>

      <div style={{ overflowX: 'auto', minHeight: 360 }} onClick={() => setSelected(null)}>
        <div style={{ minWidth: 640 }}>
          <div style={{ display: 'grid', gridTemplateColumns: TRASH_GRID, padding: '7px 14px', fontSize: 12, color: '#6B7280', borderBottom: '1px solid #F1F3F5' }}>
            <span>이름</span><span>원래 위치</span><span>삭제한 날짜</span><span>삭제한 사람</span><span style={{ textAlign: 'right' }}>크기</span>
          </div>
          {q.isLoading ? (
            <div style={{ padding: 40, textAlign: 'center' }}><Spinner size={22} /></div>
          ) : items.length === 0 ? (
            <div style={{ padding: 48, textAlign: 'center', color: '#9CA3AF', fontSize: 13 }}>휴지통이 비어 있습니다.</div>
          ) : (
            <div style={{ padding: '4px 6px' }}>
              {items.map((it) => {
                const on = it.id === selected;
                return (
                  <div key={it.id} role="option" aria-selected={on} title={`${it.originalName}\n소유자: ${it.ownerName || '—'}`}
                    onClick={(e) => { e.stopPropagation(); setSelected(it.id); }}
                    onDoubleClick={() => canRestore(it) && restore(it)}
                    onContextMenu={(e) => { e.preventDefault(); e.stopPropagation(); setSelected(it.id); setCtx({ x: e.clientX, y: e.clientY, item: it }); }}
                    style={{ display: 'grid', gridTemplateColumns: TRASH_GRID, alignItems: 'center', padding: '5px 8px', borderRadius: 4, fontSize: 13, userSelect: 'none', background: on ? '#DCEBFF' : undefined }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
                      {it.folder ? <FolderIcon /> : <FileIcon name={it.originalName} />}
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{it.originalName}</span>
                    </span>
                    <span style={cell}>{['자료실', ...it.originalPath.split('/').filter(Boolean)].join(' › ')}</span>
                    <span style={cell}>{fmtDate(it.deletedAt)}</span>
                    <span style={cell}>{it.deletedByName || '—'}</span>
                    <span style={{ ...cell, textAlign: 'right' }}>{fmtSize(it.size)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: '6px 14px', borderTop: '1px solid #EEF0F3', fontSize: 12, color: '#6B7280', background: '#FAFBFC' }}>
        항목 {items.length}개 · 복원은 소유자·삭제한 사람·관리자, 영구 삭제는 관리자만 할 수 있습니다.
      </div>

      {ctx && (
        <ContextMenu x={ctx.x} y={ctx.y} onClose={() => setCtx(null)}>
          <MenuItem disabled={!canRestore(ctx.item) || !!busy} onClick={() => { const it = ctx.item; setCtx(null); restore(it); }}><ArrowResetRegular style={ic(14)} /> 복원</MenuItem>
          {isAdmin && <MenuItem danger disabled={!!busy} onClick={() => { const it = ctx.item; setCtx(null); setConfirm({ kind: 'purge', item: it }); }}>영구 삭제</MenuItem>}
        </ContextMenu>
      )}
      {confirm && (
        <ConfirmDialog danger title={confirm.kind === 'empty' ? '휴지통 비우기' : '영구 삭제'} confirmLabel={confirm.kind === 'empty' ? '모두 영구 삭제' : '영구 삭제'}
          message={confirm.kind === 'empty' ? `휴지통의 항목 ${items.length}개를 영구 삭제할까요? 자료실에서는 되돌릴 수 없습니다.` : `'${confirm.item.originalName}'을(를) 영구 삭제할까요? 자료실에서는 되돌릴 수 없습니다.`}
          onConfirm={() => { const c = confirm; setConfirm(null); if (c.kind === 'empty') empty(); else purge(c.item); }}
          onCancel={() => setConfirm(null)} />
      )}
    </div>
  );
}
